import { useEffect } from 'react';
import {
  getPointerCoords, BOARD_WIDTH, BOARD_HEIGHT,
} from '../utils/whiteboardUtils';
import { getShapeBoundingBox, isPointInShape } from '../components/shapes';

export const useWhiteboardBoardHandlers = ({
  isStudent,
  svgRef, panRef, pan,
  shapes, setShapes,
  selectedShape, setSelectedShape,
  selectedShapes, setSelectedShapes,
  selectionStartRef, isBoxSelectingRef,
  selectionRectRef, selectionCoordRef,
  isGroupDragging, setIsGroupDragging, groupDragStart,
  setDragged,
  tool,
  shapeToolbar, setShapeToolbar,
  lastMousePosition,
  spacePressed, isPanning, lastPan, updatePan,
  isDragging, resizing,
}) => {
  // Apply rubber-band rect style directly to the DOM node
  const applySelectionRect = (x1, y1, x2, y2) => {
    const el = selectionRectRef?.current;
    if (!el) return;
    const p = panRef.current;
    const left = Math.min(x1, x2) * p.scale + p.x;
    const top  = Math.min(y1, y2) * p.scale + p.y;
    const w    = Math.abs(x2 - x1) * p.scale;
    const h    = Math.abs(y2 - y1) * p.scale;
    el.style.display = 'block';
    el.style.left    = `${left}px`;
    el.style.top     = `${top}px`;
    el.style.width   = `${w}px`;
    el.style.height  = `${h}px`;
  };

  const hideSelectionRect = () => {
    const el = selectionRectRef?.current;
    if (el) el.style.display = 'none';
  };

  // --- Space-press pan handlers (registered globally) ---
  function handleMouseDown(e) {
    if (tool === 'select' && (selectedShapes.length > 0 || selectedShape)) return;

    if (spacePressed) {
      e.preventDefault();
      isPanning.current = true;
      document.body.style.cursor = 'grabbing';
      lastPan.current = {
        x: e.touches ? e.touches[0].clientX : e.clientX,
        y: e.touches ? e.touches[0].clientY : e.clientY,
      };
    }
  }

  function handleMouseMove(e) {
    if (tool === 'select' && (selectedShapes.length > 0 || selectedShape)) return;

    if (isPanning.current && spacePressed) {
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = x - lastPan.current.x;
      const dy = y - lastPan.current.y;
      updatePan(p => ({
        ...p,
        x: Math.max(Math.min(p.x + dx, 0), -(BOARD_WIDTH * p.scale - window.innerWidth)),
        y: Math.max(Math.min(p.y + dy, 0), -(BOARD_HEIGHT * p.scale - window.innerHeight)),
      }));
      lastPan.current = { x, y };
    }
  }

  function handleMouseUp() {
    if (tool === 'select' && (selectedShapes.length > 0 || selectedShape)) return;

    if (isPanning.current) {
      isPanning.current = false;
      document.body.style.cursor = spacePressed ? 'grab' : 'default';
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, spacePressed, resizing]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Board container handlers ---
  function handleBoardMouseDown(e) {
    const { x, y } = getPointerCoords(e, panRef.current, svgRef);
    lastMousePosition.current = { x, y };

    const target = e.target;
    const isResizeHandle = target.hasAttribute('data-resize-handle') ||
      target.closest('[data-resize-handle]') ||
      target.closest('circle[style*="cursor"]') ||
      target.closest('rect[style*="cursor"]') ||
      target.closest('circle[style*="z-index: 1000"]');
    if (isResizeHandle) return;

    const isGroupSelectionFrame = target.closest('svg[style*="z-index: 12"]') ||
      target.closest('line[stroke="#3b82f6"]');
    if (isGroupSelectionFrame) return;

    if (selectedShape) {
      const shape = shapes.find(s => s.id === selectedShape);
      if (shape) {
        const box = getShapeBoundingBox(shape);
        const { x: cx, y: cy } = getPointerCoords(e, panRef.current, svgRef);
        const handleSize = 8;
        const corners = [
          { x: box.x - handleSize, y: box.y - handleSize },
          { x: box.x + box.width + handleSize, y: box.y - handleSize },
          { x: box.x - handleSize, y: box.y + box.height + handleSize },
          { x: box.x + box.width + handleSize, y: box.y + box.height + handleSize },
        ];
        for (const corner of corners) {
          if (Math.sqrt((cx - corner.x) ** 2 + (cy - corner.y) ** 2) <= 8) return;
        }
      }
    }

    const isTextElement = target.closest('[data-shape-id]') || target.closest('.sticker-foreign');
    if (isTextElement) return;

    // Track effective empty state — accounts for deselection that happens inside this handler
    // (React state updates don't apply until re-render, so we can't rely on selectedShape closure)
    let effectivelyEmpty = !selectedShape && selectedShapes.length === 0;

    if (tool === 'select' && (selectedShape || selectedShapes.length > 0)) {
      const { x: px, y: py } = getPointerCoords(e, panRef.current, svgRef);
      let clickedOnShape = false;
      for (let i = shapes.length - 1; i >= 0; i--) {
        if (isPointInShape(px, py, shapes[i])) { clickedOnShape = true; break; }
      }

      if (!clickedOnShape) {
        setSelectedShape(null);
        setDragged(null);
        setSelectedShapes([]);
        selectionCoordRef.current = null;
        selectionStartRef.current = null;
        isBoxSelectingRef.current = false;
        hideSelectionRect();
        document.body.style.cursor = 'default';
        effectivelyEmpty = true;
      } else if (selectedShapes.length > 0) {
        const { x: qx, y: qy } = getPointerCoords(e, panRef.current, svgRef);
        let clickedOnSelectedShape = false;
        for (const sid of selectedShapes) {
          const s = shapes.find(sh => sh.id === sid);
          if (s && isPointInShape(qx, qy, s)) { clickedOnSelectedShape = true; break; }
        }
        if (!clickedOnSelectedShape) {
          setSelectedShapes([]);
          selectionCoordRef.current = null;
          selectionStartRef.current = null;
          isBoxSelectingRef.current = false;
          hideSelectionRect();
          document.body.style.cursor = 'default';
          for (let i = shapes.length - 1; i >= 0; i--) {
            if (isPointInShape(qx, qy, shapes[i])) { setSelectedShape(shapes[i].id); break; }
          }
        }
      }
    }

    if (tool === 'select' && e.button === 0 && effectivelyEmpty && !isBoxSelectingRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const bx = (e.clientX - rect.left - pan.x) / pan.scale;
      const by = (e.clientY - rect.top - pan.y) / pan.scale;
      selectionStartRef.current = { x: bx, y: by };
      selectionCoordRef.current = { x1: bx, y1: by, x2: bx, y2: by };
      isBoxSelectingRef.current = true;
      applySelectionRect(bx, by, bx, by);
    }

    // Only start board-drag when NOT entering rubber-band — rubber-band sets isBoxSelectingRef=true above
    if (tool === 'select' && effectivelyEmpty && !isBoxSelectingRef.current) {
      const { x: dx, y: dy } = getPointerCoords(e, panRef.current, svgRef);
      setDragged({ id: 'board', offsetX: dx, offsetY: dy });
    }

    if (shapeToolbar) setShapeToolbar(null);
  }

  function handleBoardMouseMove(e) {
    const { x, y } = getPointerCoords(e, panRef.current, svgRef);
    lastMousePosition.current = { x, y };

    // Update rubber-band rect via direct DOM mutation — no setState
    if (isBoxSelectingRef.current && selectionCoordRef.current && tool === 'select' && !selectedShape && selectedShapes.length === 0) {
      const rect = svgRef.current.getBoundingClientRect();
      const bx = (e.clientX - rect.left - pan.x) / pan.scale;
      const by = (e.clientY - rect.top - pan.y) / pan.scale;
      selectionCoordRef.current.x2 = bx;
      selectionCoordRef.current.y2 = by;
      applySelectionRect(selectionCoordRef.current.x1, selectionCoordRef.current.y1, bx, by);
    }

    // Cursor: show grab when hovering over selected group
    if (tool === 'select' && selectedShapes.length > 0) {
      const group = shapes.filter(s => selectedShapes.includes(s.id));
      const inArea = group.some(s => {
        const box = getShapeBoundingBox(s);
        return x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height;
      });
      document.body.style.cursor = inArea ? (isGroupDragging ? 'grabbing' : 'grab') : 'default';
    }

    if (isGroupDragging && groupDragStart.current && groupDragStart.current.shapes && groupDragStart.current.shapes.length > 0) {
      const rect = svgRef.current.getBoundingClientRect();
      const gx = (e.clientX - rect.left - pan.x) / pan.scale;
      const gy = (e.clientY - rect.top - pan.y) / pan.scale;
      const dx = gx - groupDragStart.current.x;
      const dy = gy - groupDragStart.current.y;
      setShapes(prev => (prev || []).map(s => {
        if (!selectedShapes.includes(s.id)) return s;
        const orig = groupDragStart.current.shapes.find(ss => ss.id === s.id);
        if (!orig) return s;
        if (s.type === 'line' || s.type === 'arrow') {
          return { ...s, x1: orig.x1 + dx, y1: orig.y1 + dy, x2: orig.x2 + dx, y2: orig.y2 + dy };
        } else if (s.type === 'pencil') {
          return { ...s, points: orig.points.map(([px, py]) => [px + dx, py + dy]) };
        }
        return { ...s, x: orig.x + dx, y: orig.y + dy };
      }));
    }
  }

  function handleBoardMouseUp(e) {
    if (isBoxSelectingRef.current && selectionCoordRef.current && tool === 'select' && !selectedShape && selectedShapes.length === 0) {
      const { x1, y1, x2, y2 } = selectionCoordRef.current;
      const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);

      hideSelectionRect();
      selectionCoordRef.current = null;
      selectionStartRef.current = null;
      isBoxSelectingRef.current = false;

      const boardBounds = { minX: 0, maxX: BOARD_WIDTH, minY: 0, maxY: BOARD_HEIGHT };
      if (maxX < boardBounds.minX || minX > boardBounds.maxX || maxY < boardBounds.minY || minY > boardBounds.maxY) {
        document.body.style.cursor = 'default';
        return;
      }
      if (maxX - minX > 2000 || maxY - minY > 2000) {
        document.body.style.cursor = 'default';
        return;
      }
      if (maxX - minX < 10 && maxY - minY < 10) {
        document.body.style.cursor = 'default';
        return;
      }

      const screenW = window.innerWidth / pan.scale;
      const screenH = window.innerHeight / pan.scale;

      const selected = shapes.filter(s => {
        if (!s || !s.type || typeof s.x === 'undefined' || typeof s.y === 'undefined') return false;
        if (s.type === 'grid' || s.type === 'gridPoint' || s.type === 'background') return false;
        if (['container', 'ui', 'interface', 'toolbar', 'menu', 'panel'].includes(s.type)) return false;
        const box = getShapeBoundingBox(s);
        if (box.width > 5000 || box.height > 5000) return false;
        if (box.width < 2 && box.height < 2) return false;
        if (box.x < 50 && box.y < 50) return false;
        if (box.width > screenW * 0.9 && box.height > screenH * 0.9) return false;
        if (box.x === 0 && box.y === 0 && (box.width > 1000 || box.height > 1000)) return false;
        const intersects = box.x < maxX && box.x + box.width > minX && box.y < maxY && box.y + box.height > minY;
        const cx = box.x + box.width / 2, cy = box.y + box.height / 2;
        const centerInBox = cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
        const isSmall = box.width < 20 || box.height < 20;
        return intersects || (isSmall && centerInBox);
      }).map(s => s.id);

      if (e.shiftKey) {
        setSelectedShapes(prev => {
          const next = [...prev];
          selected.forEach(id => { if (!next.includes(id)) next.push(id); });
          return next;
        });
        setSelectedShape(null);
      } else if (selected.length === 1) {
        setSelectedShape(selected[0]);
        setSelectedShapes([]);
      } else {
        setSelectedShapes(selected);
        setSelectedShape(null);
      }

      document.body.style.cursor = 'default';
    }

    if (isGroupDragging) {
      setIsGroupDragging(false);
      groupDragStart.current = null;
      document.body.style.cursor = 'default';
    }

    isBoxSelectingRef.current = false;
    selectionCoordRef.current = null;
    hideSelectionRect();
    setDragged(null);
  }

  return { handleBoardMouseDown, handleBoardMouseMove, handleBoardMouseUp };
};
